// ────────────────────────────────────────────────────────────────────────────
// Azure Maps tokens for the browser.
//
// The Azure Maps subscription key stays on the server; the browser gets a
// short-lived Microsoft Entra token from Directory and sends it as a bearer
// token with the Maps account's client id. One token is shared by every map
// and route call on the page and renewed a few minutes before it expires.
// ────────────────────────────────────────────────────────────────────────────
import { directoryApi, type CoordinatesSourceValue } from './client';

/**
 * One end of a road leg, from an address: its coordinates, and its Google place
 * id ONLY when the coordinates came from Google — so the route cache keys it by
 * the place and stores no Google coordinates. A pin set by hand ("Manual") goes
 * by its coordinates. Null when the address has no coordinates.
 */
export function roadLegEnd(address: {
  id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  placeId?: string | null;
  coordinatesSource?: CoordinatesSourceValue | null;
}): {
  latitude: number;
  longitude: number;
  placeId: string | null;
  addressId: string | null;
} | null {
  if (address.latitude == null || address.longitude == null) return null;
  const google = address.coordinatesSource !== 'Manual' && !!address.placeId?.trim();
  return {
    latitude: address.latitude,
    longitude: address.longitude,
    placeId: google ? address.placeId!.trim() : null,
    addressId: address.id ?? null,
  };
}

const RENEW_BEFORE_MS = 5 * 60 * 1000;

let current: { token: string; expiresAt: number } | null = null;
let inFlight: Promise<string> | null = null;

/** A valid Azure Maps token, fetched from Directory only when the cached one is near expiry. */
export function getAzureMapsToken(): Promise<string> {
  if (current && current.expiresAt - Date.now() > RENEW_BEFORE_MS)
    return Promise.resolve(current.token);
  inFlight ??= directoryApi
    .getAzureMapsTokenRaw()
    .then((t) => {
      current = { token: t.token, expiresAt: Date.parse(t.expiresOnUtc) };
      return t.token;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Headers for a direct call to an Azure Maps REST API from the browser. */
export async function azureMapsAuthHeaders(clientId: string): Promise<Record<string, string>> {
  return {
    Authorization: `Bearer ${await getAzureMapsToken()}`,
    'x-ms-client-id': clientId,
  };
}
